import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { trainers, siteConfig } from '../data/mockData';
import { Award, Users, Target, Zap } from 'lucide-react';

const ChiSiamo = () => {
  const values = [
    {
      id: "risultati-primo",
      icon: Target,
      title: "Risultati Prima di Tutto",
      description: "Ogni programma, ogni sessione, ogni esercizio ha un obiettivo chiaro: portarti dove vuoi arrivare."
    },
    {
      id: "disciplina-metodo",
      icon: Zap,
      title: "Disciplina & Metodo",
      description: "Non lasciamo nulla al caso. Periodizzazione scientifica, progressioni controllate, monitoraggio costante."
    },
    {
      id: "personalizzazione-totale",
      icon: Users,
      title: "Personalizzazione Totale",
      description: "Non esistono programmi standard. Tu sei unico, il tuo percorso deve esserlo."
    },
    {
      id: "professionalita-assoluta",
      icon: Award,
      title: "Professionalità Assoluta",
      description: "Certificazioni internazionali, formazione continua, esperienza sul campo. Siamo i migliori in quello che facciamo."
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1583454110551-21f2fa2afe61"
            alt="Our Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>

        <div className="relative container mx-auto px-4 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Performance, movimento e <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">competenza</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              PERFORMOVE nasce con un obiettivo chiaro: migliorare la performance attraverso il movimento.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-8">
              La Nostra <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Storia</span>
            </h2>
            <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
              <p>
                Sono un <strong className="text-white">Personal Trainer laureato in Scienze Motorie</strong> con specializzazione in ambito sportivo e ortopedico.
              </p>
              <p>
                Lavoro ogni giorno con atleti e sportivi per sviluppare forza, prevenire infortuni e ottimizzare il ritorno all'attività.
              </p>
              <p className="text-xl font-semibold text-white mt-8">
                Il mio approccio unisce:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start space-x-3">
                  <span className="text-orange-500">•</span>
                  <span>Allenamento funzionale</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-orange-500">•</span>
                  <span>Strength & Conditioning</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-orange-500">•</span>
                  <span>Prevenzione e riatletizzazione</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-orange-500">•</span>
                  <span>Metodo basato su evidenze scientifiche</span>
                </li>
              </ul>
              <p className="text-xl font-bold text-white pt-6 border-t border-white/10 mt-8">
                Non si tratta solo di allenarsi, ma di costruire un corpo capace di performare nel tempo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              I Nostri <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Valori</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Questi principi guidano ogni decisione, ogni allenamento, ogni interazione con i nostri clienti.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <Card key={value.id} className="bg-white/5 border border-white/10 hover:border-orange-500/50 transition-all duration-300 group backdrop-blur-sm">
                  <CardContent className="p-6 md:p-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-green-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-black" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {value.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trainers Section */}
      <section className="py-24 bg-gradient-to-b from-zinc-950 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Il Nostro <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Team</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Professionisti certificati, esperienza internazionale, passione per i risultati.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto">
            {trainers.map((trainer) => (
              <Card key={trainer.id} className="bg-white/5 border border-white/10 overflow-hidden hover:border-orange-500/50 transition-all duration-300 group backdrop-blur-sm">
                <div className="relative h-72 sm:h-80 overflow-hidden">
                  <img
                    src={trainer.image}
                    alt={trainer.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-black text-white mb-2">
                    {trainer.name}
                  </h3>
                  <p className="text-orange-500 font-bold mb-4 uppercase text-sm tracking-wide">
                    {trainer.role}
                  </p>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {trainer.description}
                  </p>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Esperienza</span>
                      <p className="text-white font-bold">{trainer.experience}</p>
                    </div>
                    {trainer.certifications.length > 0 && (
                      <div>
                        <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Certificazioni</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {trainer.certifications.map((cert, i) => (
                            <span key={`${trainer.id}-cert-${i}`} className="px-3 py-1 bg-gradient-to-r from-orange-500/20 to-green-400/20 border border-orange-500/30 rounded-full text-orange-500 text-xs font-bold">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Difference Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-12 text-center">
              La <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Differenza</span> Performove
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Traditional Gym */}
              <Card className="bg-red-900/10 border border-red-500/30 backdrop-blur-sm">
                <CardContent className="p-6 md:p-8">
                  <h3 className="text-2xl font-bold text-red-400 mb-6">❌ Palestra Tradizionale</h3>
                  <ul className="space-y-3 text-gray-400">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Programmi standardizzati per tutti</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Nessuna supervisione personalizzata</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Ambienti affollati e dispersivi</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Mancanza di accountability</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Risultati mediocri o inesistenti</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Performove */}
              <Card className="bg-gradient-to-br from-orange-500/10 to-green-400/10 border border-orange-500/50 backdrop-blur-sm">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400 mb-6">✓ Performove Training_LAB</h3>
                  <ul className="space-y-3 text-gray-200">
                    <li className="flex items-start">
                      <span className="mr-2 text-green-400">✓</span>
                      <span>Programmi 100% personalizzati</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-400">✓</span>
                      <span>Attenzione 1:1 ogni sessione</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-400">✓</span>
                      <span>Ambiente professionale e riservato</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-400">✓</span>
                      <span>Monitoraggio continuo dei progressi</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-400">✓</span>
                      <span>Risultati garantiti con impegno</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamo;
